import * as React from 'react';
import { Fileupload } from 'sos-common/js/components/Fileupload/Fileupload';
import { FormFieldFile } from 'sos-common/js/commonTypes/formField.types';
import {
  StatsbidragFileUploaderProps,
  StatsbidragFileUploaderState,
  STATSBIDRAG_EPIFORMFILE_MIMETYPES,
  StatsbidragFileUploaderServerResponse,
  NO_UPLOADED_FILES,
} from './StatsbidragFileUploader.types';
import { FileExtendedDetails, FileState } from 'sos-common/js/commonTypes/fileupload.types';
import { ALL_MANDATORY, THIS_MANDATORY } from '../CalculationComponents/calculationComponents.errorMessages';
import EventService from '../CalculationComponents/calculationComponents.eventservice';
import ValidationService from '../../services/validation.service';
import { ResponseError } from 'sos-common/js/utils/ResponseError';
import { postFile, getFiles, deleteFile } from 'sos-eservice-common/js/services/files.service';
import {
  getErrorBodyJwtTokenTimeout,
  getErrorBodyVipSPSessionTimeout,
} from 'sos-eservice-common/js/config/dialogueErrorBodies';
import { POST_ERROR } from '../StatsbidragSubmitAction/StatsbidragSubmitAction';

const API_ENDPOINT = '/_api/sbgformfile';

/**
 * Contains the logic for uploading and deleting a file, and getting sent files of a file field.
 */
export class StatsbidragFileUploader extends React.Component<
  StatsbidragFileUploaderProps,
  StatsbidragFileUploaderState
> {
  constructor(props: StatsbidragFileUploaderProps) {
    super(props);
    this.state = {
      fileUploadState: [] as FileExtendedDetails[],
      clientError: null,
      serverResponseObject: null,
      isValid: true,
      isTouched: false,
    };
  }

  componentDidMount() {
    this.getCurrentAttachments();

    EventService.subscribe('form-is-valid', (e: CustomEvent) => {
      if (!this.state.isValid) {
        this.setState({
          isTouched: true,
          clientError: this.props.isRequired ? { error: null, errorMessage: THIS_MANDATORY } : null,
        });
      }
    });
  }

  getCurrentAttachments = () => {
    const { type, caseId, fieldId, isRequired, formElementName } = this.props;
    const apiEndpoint = API_ENDPOINT + `/get?caseId=${caseId}&fieldId=${fieldId}`;

    if (type === 'Preview') return;

    getFiles<StatsbidragFileUploaderServerResponse>(apiEndpoint).then((response) => {
      if (response instanceof ResponseError) {
        if (isRequired) {
          this.setState(
            {
              isValid: !isRequired,
              clientError: {
                isValid: !isRequired,
                error: null,
                errorMessage: response.body.errorMessage || 'Ett ohanterat fel inträffade.',
              },
            },
            () => {
              ValidationService.validateStepViaElementName(formElementName);
            }
          );
        }
      } else {
        if (response.Files && response.Files.length > 0) {
          let fileExtendedDetails: FileExtendedDetails[] = [];
          response.Files.forEach((value) => {
            fileExtendedDetails.push({
              OriginalFilename: value.Name,
              FileId: value.BilagaId,
              status: FileState.SETTLED,
            });
          });

          this.setState({
            fileUploadState: fileExtendedDetails,
            serverResponseObject: response,
            isValid: true,
          });
        } else {
          this.setState({
            isValid: !isRequired,
          });
        }
      }
    });
  };

  fireDialogueError = (error: ResponseError) => {
    if (error.body && error.body.detailCode) {
      switch (error.body.detailCode) {
        // SESSION ERROR
        case '401.1':
          // VIP (SP) SESSION TIMED OUT
          EventService.fire('dialogue-error', { detail: getErrorBodyVipSPSessionTimeout(error.body.redirectUri) });
          break;
        case '401.2':
          // JWT TOKEN TIMED OUT
          EventService.fire('dialogue-error', { detail: getErrorBodyJwtTokenTimeout(error.body.redirectUri) });
          break;
      }
    }
  };

  onAddFile = (file: FormFieldFile) => {
    const { type, caseId, fieldId, fieldName, isRequired, formElementName } = this.props;
    const apiEndpoint = API_ENDPOINT + '/attach';
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('fieldId', fieldId);
    formData.append('fieldName', fieldName);

    if (type === 'Preview') return;

    //QUESTION: Do we really need to est the filevalue before

    postFile<StatsbidragFileUploaderServerResponse>(file, apiEndpoint, formData).then((response) => {
      if (response instanceof ResponseError) {
        //If the errors are session problem (logged out), we fire the dialouge error.
        this.fireDialogueError(response);
        EventService.fire(POST_ERROR, null);

        this.setState(
          {
            isTouched: true,
            isValid: !isRequired,
            clientError: {
              isValid: !isRequired,
              error: null,
              errorMessage: response.body.errorMessage || 'Ett ohanterat fel inträffade.',
            },
          },
          () => {
            ValidationService.validateStepViaElementName(formElementName);
          }
        );
      } else {
        this.setState(
          {
            fileUploadState: response.fileUploadValue,
            serverResponseObject: response.serverResponse,
            clientError: null,
            isValid: true,
            isTouched: true,
          },
          () => {
            ValidationService.validateStepViaElementName(formElementName);
          }
        );
      }
    });
  };

  onDeleteFile = (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => {
    const { caseId, isRequired, formElementName } = this.props;
    const { serverResponseObject } = this.state;
    const apiEndpoint = API_ENDPOINT + '/delete';
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('bilagaId', serverResponseObject.Files[0].BilagaId);

    if (this.props.type === 'Preview') return;
    deleteFile(apiEndpoint, formData).then((response) => {
      if (response instanceof ResponseError) {
        this.setState(
          {
            isTouched: true,
            isValid: !isRequired,
            clientError: {
              isValid: !isRequired,
              error: null,
              errorMessage: response.body.errorMessage || 'Ett ohanterat fel inträffade.',
            },
          },
          () => {
            ValidationService.validateStepViaElementName(formElementName);
          }
        );
      } else {
        let newFileUploadState = this.state.fileUploadState.slice();
        let index = newFileUploadState.findIndex((val) => {
          return val.FileId === response;
        });
        newFileUploadState.splice(index, 1);
        this.setState(
          {
            isTouched: true,
            fileUploadState: newFileUploadState,
            clientError: isRequired ? { error: null, errorMessage: THIS_MANDATORY } : null,
            isValid: !isRequired,
          },
          () => {
            ValidationService.validateStepViaElementName(formElementName);
          }
        );
      }
    });
  };

  /**
   * This returns the uploaded file name or NO_UPLOADED_FILES constant. Used only in printing.
   */
  getFileNameForPrint = () => {
    if (this.state.serverResponseObject === null) {
      if (this.props.autoFillValues[0]) {
        return this.props.autoFillValues[0];
      }
      return NO_UPLOADED_FILES;
    }

    let fileArray = this.state.fileUploadState;
    if (fileArray.length === 0) return NO_UPLOADED_FILES;

    return fileArray[0].OriginalFilename;
  };

  getFileNameForXmlFile = () => {
    if (this.state.serverResponseObject === null) {
      let fileName = this.props.autoFillValues[0];
      if (fileName && fileName !== NO_UPLOADED_FILES) {
        return fileName;
      }
      return '';
    }

    let fileArray = this.state.fileUploadState;
    if (fileArray.length === 0) return '';

    return fileArray[0].OriginalFilename;
  };

  public render() {
    const { fileHeading, maxFile, maxFileSizeInMb, mimeTypes } = this.props;

    const fileName = {
      forXmlFile: this.getFileNameForXmlFile(),
      forPrint: this.getFileNameForPrint(),
    };

    return (
      <div>
        <Fileupload
          disabled={false}
          max={maxFile}
          heading={fileHeading}
          upload="Ladda upp fil"
          value={this.state.fileUploadState}
          nofilesText="Du har inte laddat upp någon fil ännu"
          mimetype={mimeTypes || STATSBIDRAG_EPIFORMFILE_MIMETYPES}
          onAdd={this.onAddFile}
          onRemove={this.onDeleteFile}
          maxFileSize={(maxFileSizeInMb || 10) * 1048576}
          clientError={this.state.clientError}
          isTouched={this.state.isTouched}
        />
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={this.props.formElementName}
          id={this.props.formElementName}
          value={fileName.forXmlFile}
          readOnly
        />
        <input
          type="text"
          aria-hidden="true"
          className="only-print file-print-detail"
          name={`${this.props.formElementName}_file`}
          id={`${this.props.formElementName}_file`}
          value={fileName.forPrint}
          readOnly
        />
      </div>
    );
  }
}
